import { Image, Flex, VStack, Text, Box, Button } from "@chakra-ui/react";
import { Event as ApiEvent, EventCoverImage, OlaggModule, PermissionEnum } from "@olagg/db-types";
import { useForm } from "react-hook-form";
import OlaggForm from "../../../components/Forms/OlaggForm";
import OlaggFormInput from "../../../components/Forms/OlaggFormInput";
import { yupResolver } from '@hookform/resolvers/yup'
import { eventSchema } from "@olagg/validation-schemas/event.schema";
import OlaggFormSelect from "../../../components/Forms/OlaggFormSelect";
import { useEffect, useState } from "react";
import useHosts from "../hooks/useHosts";
import useEvents from "../hooks/useEvents";
import OlaggMarkdown from "@olagg/ui-kit/OlaggMarkdown";
import useRoles from "../../../features/Roles/hooks/useRoles";
import { useAdminGames } from "@olagg/api-hooks";
import useBlockchainTokenCatalog from "../../BlockchainTokenCatalog/hooks/useBlockchainTokenCatalog";
import useBlockchainNetwork from "../../BlockchainTokenCatalog/hooks/useBlockchainNetwork";
import { fromLocalDateTimeToUTC, toLocalTimezone } from "../../../utils";

type IProps = {
    event?: ApiEvent;
    onSubmit: (dataForm: any) => void;
};

const EventForm = ({ onSubmit, event }: IProps) => {
    const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const [newsroomOptions, setNewsroomOptions] = useState<{ label: string; value: string }[]>([]);
    const [isRegional, setIsRegional] = useState<boolean>(event?.regional || false);
    const [coverImage, setCoverImage] = useState<EventCoverImage>();
    const [defaultHostOptions, setDefaultHostOptions] = useState<{ label: string; value: string }[]>([]);
    const [defaultGameOptions, setDefaultGameOptions] = useState<{ label: string; value: string }[]>([]);
    const [defaultNewsroomOptions, setDefaultNewsroomOptions] = useState<{ label?: string; value: string }[]>([]);
    const [description, setDescription] = useState<{ es: string; en: string; pt: string }>({
        es: event?.description.es || '',
        en: event?.description.en || '',
        pt: event?.description.pt || ''
    });
    const { all, blockchainTokens } = useBlockchainTokenCatalog();
    const { getHosts } = useHosts();
    const { getNewsroomEvents, getNewsroomEvent } = useEvents();
    const { asyncGameOptions } = useAdminGames({
        page: 1,
        limit: 5
    });
    const { missingPermission } = useRoles();
    const [selectedNetwork, setSelectedNetwork] = useState<number | null>(null);
    const { all: allNetworks, blockchainNetworks } = useBlockchainNetwork();
    const filteredBlockchainTokens = selectedNetwork
        ? blockchainTokens?.filter(token => token.network.id === selectedNetwork)
        : blockchainTokens;
    const useFormMethods = useForm<Partial<ApiEvent>>({
        resolver: yupResolver(eventSchema),
        mode: 'onChange',
        defaultValues: {
            ...event,
            startsAt: event?.startsAt ? toLocalTimezone(event.startsAt) : undefined,
            endsAt: event?.endsAt ? toLocalTimezone(event.endsAt) : undefined,
            rescheduled: event?.rescheduled ? {
                newStartsAt: toLocalTimezone(event.rescheduled.newStartsAt)
            } : undefined
        }
    });

    const getHostOptions = async (name?: string) =>
        getHosts({ limit: 10, page: 1, name })
            .then(response => {
                const options = response.hosts.map((host: any) => ({
                    label: host.name,
                    value: host.id
                }));
                return options;
            }).catch(error => {
                console.error('Error fetching hosts:', error);
                return [];
            });


    const getNewsroomEventsOptions = async (title?: string) => {
        const options = await getNewsroomEvents({ title, page: 1, limit: 5 }).then(response => {
            console.log('Newsroom events response:', response);
            if (Array.isArray(response) && response.length > 0) {
                return response.map((event: any) => ({
                    label: event.title,
                    value: event.id || ''
                }));
            }
            return [{ label: response.title, value: response.if }];
        }).catch(error => {
            console.error('Error fetching newsroom events:', error);
            return [{ label: 'No events found', value: '' }];
        });

        setNewsroomOptions(options);
        return options
    }


    const initialHostOptions = event?.host ? [{
        label: event.host.name,
        value: event.host.id
    }] : defaultHostOptions;

    const initialNewsroomOptions = !event?.newsroomId ? defaultNewsroomOptions : [{
        label: event.title.es,
        value: event.newsroomId
    }];

    const initialGameOptions = event?.game ? [{
        label: event.game.title,
        value: event.game.id
    }] : defaultGameOptions;

    const handleSubmit = (dataForm: any) => {
        const formData = {
            ...dataForm,
            hostId: dataForm.host?.id,
            gameId: dataForm.game?.id,
            startsAt: fromLocalDateTimeToUTC(dataForm.startsAt),
            endsAt: fromLocalDateTimeToUTC(dataForm.endsAt),
            rescheduled: dataForm.rescheduled?.newStartsAt ? {
                newStartsAt: fromLocalDateTimeToUTC(dataForm.rescheduled.newStartsAt)
            } : {}
        };
        console.log(formData)
        onSubmit(formData);
    };

    useEffect(() => {
        if (selectedNetwork === null) {
            useFormMethods.setValue('prize', undefined);
        }
    }, [selectedNetwork, useFormMethods]);

    useEffect(() => {
        setSelectedNetwork(event?.prize?.network || null);
        all({
            page: 1,
            limit: 100
        });
        allNetworks({ page: 1, limit: 100 });
        getNewsroomEventsOptions().then(setDefaultNewsroomOptions);
        getHostOptions().then(setDefaultHostOptions);
        asyncGameOptions("").then((r) => setDefaultGameOptions(r as any));
    }, []);

    return (
        <OlaggForm
            useFormMethods={useFormMethods}
            onSubmit={handleSubmit}
            style={{
                width: '100%'
            }}
        >
            <VStack color={'white'} w={'full'} maxW='800px' margin='0 auto'>
                <OlaggFormInput
                    label="Ingresa el id del evento de NewsRoom"
                    controlName='newsroomId'
                    description="Si el evento ya existe en NewsRoom, puedes ingresar su id aquí"
                    required={false}
                />
                <OlaggFormSelect
                    label="Seleccionar host"
                    controlName="host.id"
                    description="Selecciona el host del evento"
                    isSearchable={true}
                    staticOptions={initialHostOptions}
                    asyncOptions={getHostOptions}
                    newVersion
                    required={true}
                />
                <OlaggFormSelect
                    label="Seleccionar Juego"
                    controlName="game.id"
                    description="Selecciona el juego del evento"
                    isSearchable={true}
                    asyncOptions={asyncGameOptions}
                    staticOptions={initialGameOptions}
                    newVersion
                    required={false}
                />
                <OlaggFormInput
                    label='Titulo (ES)'
                    controlName='title.es'
                    description='El título del evento en español'
                    required={true}
                />

                <OlaggFormInput
                    label='Titulo (EN)'
                    controlName='title.en'
                    description='El título del evento en ingles'
                    required={true}
                />

                <OlaggFormInput
                    label='Titulo (PT)'
                    controlName='title.pt'
                    description='El título del evento en portugués'
                    required={true}
                />

                <OlaggFormInput
                    label='Slug'
                    controlName='slug'
                    description='El slug del evento, utilizado en la URL'
                    required={true}
                />

                <Flex direction={"column"} w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4} gap="4">
                    <OlaggFormInput
                        label='Descripción (ES)'
                        controlName='description.es'
                        description='La descripción del evento en español'
                        inputType='textarea'
                        onChange={(e) => setDescription({ ...description, es: e.target.value })}
                    />

                    <Text lineHeight='normal' fontWeight={600} mb='2'>
                        Descripción (Vista previa)
                    </Text>
                    <Box w='full' mb='5' border='1px solid white' minH='10' borderRadius='5' p='3'>
                        <OlaggMarkdown>
                            {description.es || ''}
                        </OlaggMarkdown>
                    </Box>
                </Flex>

                <Flex direction={"column"} w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4} gap="4">
                    <OlaggFormInput
                        label='Descripción (EN)'
                        controlName='description.en'
                        description='La descripción del evento en inglés'
                        inputType='textarea'
                        onChange={(e) => setDescription({ ...description, en: e.target.value })}
                    />

                    <Text lineHeight='normal' fontWeight={600} mb='2'>
                        Descripción (Vista previa)
                    </Text>
                    <Box w='full' mb='5' border='1px solid white' minH='10' borderRadius='5' p='3'>
                        <OlaggMarkdown>
                            {description.en || ''}
                        </OlaggMarkdown>
                    </Box>
                </Flex>

                <Flex direction={"column"} w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4} gap="4">
                    <OlaggFormInput
                        label='Descripción (PT)'
                        controlName='description.pt'
                        description='La descripción del evento en portugués'
                        inputType='textarea'
                        onChange={(e) => setDescription({ ...description, pt: e.target.value })}
                    />

                    <Text lineHeight='normal' fontWeight={600} mb='2'>
                        Descripción (Vista previa)
                    </Text>
                    <Box w='full' mb='5' border='1px solid white' minH='10' borderRadius='5' p='3'>
                        <OlaggMarkdown>
                            {description.pt || ''}
                        </OlaggMarkdown>
                    </Box>
                </Flex>

                <Flex direction={"row"} w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4} gap="4">
                    <Flex direction={"column"}>
                        <OlaggFormInput
                            label='Imagen de portada (ES)'
                            controlName='coverImage.es'
                            description='La imagen de portada del evento en español'
                            required={true}
                            onChange={(e) => setCoverImage({
                                ...coverImage,
                                es: e.target.value as string
                            })}
                        />
                        <Image src={coverImage?.es} h={'210'}
                            w={'100%'}
                            objectFit='cover' />

                    </Flex>
                    <Flex direction={"column"}>
                        <OlaggFormInput
                            label='Imagen de portada (EN)'
                            controlName='coverImage.en'
                            description='La imagen de portada del evento en inglés'
                            required={true}
                            onChange={(e) => setCoverImage({
                                ...coverImage,
                                en: e.target.value as string
                            })}
                        />
                        <Image src={coverImage?.en} h={'210'}
                            w={'100%'}
                            objectFit='cover' />

                    </Flex>
                    <Flex direction={"column"}>
                        <OlaggFormInput
                            label='Imagen de portada (PT)'
                            controlName='coverImage.pt'
                            description='La imagen de portada del evento en portugués'
                            required={true}
                            onChange={(e) => setCoverImage({
                                ...coverImage,
                                pt: e.target.value
                            })}
                        />
                        <Image src={coverImage?.pt} h={'210'}
                            w={'100%'}
                            objectFit='cover' />
                    </Flex>
                </Flex>

                <Flex direction={"column"} w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4} gap="4">
                    <Text color='white' fontSize="18px" fontWeight={"bold"} mb="2">
                        🏆 Premio del Evento
                    </Text>

                    <OlaggFormSelect
                        label='Select network'
                        controlName='prize.network'
                        staticOptions={blockchainNetworks?.map(network => ({
                            value: network.id,
                            label: network.name
                        }))}
                        onChange={setSelectedNetwork}
                        newVersion
                        required={false}
                    />

                    {selectedNetwork && (
                        <OlaggFormSelect
                            label='Blockchain Token'
                            controlName='prize.blockchainTokenId'
                            staticOptions={filteredBlockchainTokens?.map(blockchainToken => ({
                                value: blockchainToken.id,
                                label: blockchainToken.name
                            }))}
                            required={selectedNetwork !== null}
                            newVersion
                        />
                    )}

                    {selectedNetwork && (
                        <OlaggFormInput
                            label='Cantidad'
                            controlName='prize.amount'
                            placeholder='Cantidad'
                            required={selectedNetwork !== null}
                        />
                    )}
                </Flex>

                <OlaggFormInput
                    label='¿Regional?'
                    controlName='regional'
                    inputType='checkbox'
                    onChange={(e) => setIsRegional(e.target.checked)}
                />

                {isRegional && <OlaggFormInput
                    label='Ubicación'
                    controlName='location'
                    placeholder="Ubicación del evento"
                />}

                <OlaggFormSelect
                    label='Tipo de evento'
                    controlName='eventType'
                    staticOptions={[{
                        label: 'Regular',
                        value: 'regular'
                    }, {
                        label: 'Recurrente',
                        value: 'recurrent'
                    }]}
                    required={true}
                    newVersion
                />

                <OlaggFormSelect
                    label='Plataforma donde se transmite'
                    controlName='streamingPlatform'
                    staticOptions={[{
                        label: 'Twitch',
                        value: 'twitch'
                    }, {
                        label: 'YouTube',
                        value: 'youtube'
                    }, {
                        label: 'Discord',
                        value: 'discord'
                    }]}
                    required={true}
                    newVersion
                />

                <OlaggFormInput
                    label='Url de la transmisión'
                    controlName='streamingLink'
                    placeholder='La URL de la transmisión del evento'
                />

                <OlaggFormInput
                    label='Disponible desde'
                    controlName='startsAt'
                    inputType='datetime'
                    description={`Zona horaria: ${currentTimeZone}`}
                    datePickerProps={{ backgroundColor: 'white' }}
                    required={true}
                />
                <OlaggFormInput
                    label='Disponible hasta'
                    controlName='endsAt'
                    inputType='datetime'
                    description={`Zona horaria: ${currentTimeZone}`}
                    datePickerProps={{ backgroundColor: 'white' }}
                    required={false}
                />
                <Flex direction={"column"} w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4} gap="4">
                    <Text color='white' fontSize="18px" fontWeight={"bold"} mb="2">
                        CTA
                    </Text>
                    <Flex gap="4" justify={"space-between"}>
                        <OlaggFormInput
                            label='CTA Texto (ES)'
                            controlName='cta.es.text'
                            placeholder='Texto del CTA en español'
                            required={false}
                        />
                        <OlaggFormInput
                            label='CTA link (ES)'
                            controlName='cta.es.link'
                            placeholder='Link del CTA en español'
                            required={false}
                        />
                    </Flex>

                    <Flex gap="4" justify={"space-between"}>
                        <OlaggFormInput
                            label='CTA Texto (EN)'
                            controlName='cta.en.text'
                            placeholder='Texto del CTA en inglés'
                            required={false}
                        />
                        <OlaggFormInput
                            label='CTA link (EN)'
                            controlName='cta.en.link'
                            placeholder='Link del CTA en inglés'
                            required={false}
                        />
                    </Flex>

                    <Flex gap="4" justify={"space-between"}>
                        <OlaggFormInput
                            label='CTA Texto (PT)'
                            controlName='cta.pt.text'
                            placeholder='Texto del CTA en portugués'
                            required={false}
                        />
                        <OlaggFormInput
                            label='CTA link (PT)'
                            controlName='cta.pt.link'
                            placeholder='Link del CTA en portugués'
                            required={false}
                        />
                    </Flex>
                </Flex>

                <Flex direction={"column"} w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4} gap="4">
                    <Text color='white' fontWeight={"bolder"}>Reprogramar</Text>
                    <i>Si este campo tiene algun valor, el evento aparecera como reprogramado</i>
                    <i>Para eliminar el label de reprogramado, dejar en blanco</i>
                    <i>El campo de "Disponible desde" se actualizara solo una vez guardado</i>

                    <OlaggFormInput
                        label='Nuevo valor para disponible desde'
                        controlName='rescheduled.newStartsAt'
                        inputType='datetime'
                        description={`Zona horaria: ${currentTimeZone}`}
                        datePickerProps={{ backgroundColor: 'white' }}
                        required={false}
                    />
                </Flex>

                <OlaggFormSelect
                    label="Estado del evento"
                    controlName="status"
                    description="Selecciona el estado del evento"
                    staticOptions={[{
                        label: 'Borrador',
                        value: 'draft'
                    }, {
                        label: 'Publicado',
                        value: 'published'
                    }, {
                        label: 'Cancelado',
                        value: 'canceled'
                    }]}
                    newVersion
                />

                <Button
                    variant={'filledGradient'}
                    type="submit"
                    w={'full'}
                    style={{
                        marginTop: '30px'
                    }}
                    isDisabled={
                        missingPermission(OlaggModule.Event, event ? PermissionEnum.EDIT : PermissionEnum.CREATE)
                    }
                >
                    Guardar
                </Button>
            </VStack>
        </OlaggForm>
    )
};

export default EventForm;